import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
function DesktopNav() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-8">
        {[1, 2, 3, 4].map(i => <div key={i} data-index={i} className="h-4 w-16 bg-muted/20 animate-pulse rounded-sm" />)}
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="text-xs font-medium uppercase tracking-[0.15em] text-muted-foreground transition-colors hover:text-foreground">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileNav({
  isOpen,
  onClose
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-40 flex flex-col bg-background/95 backdrop-blur-xl pt-24 px-6 animate-in fade-in slide-in-from-top-5 duration-300">
      <nav className="flex flex-col gap-6 items-center">
        {loading ? <div className="flex flex-col gap-4 w-full items-center">
            {[1, 2, 3, 4].map(i => <div key={i} data-index={i} className="h-6 w-32 bg-muted/20 animate-pulse rounded-sm" />)}
          </div> : menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="text-lg font-medium uppercase tracking-widest text-foreground/80 hover:text-primary transition-colors" onClick={onClose}>
              {item.label}
            </Link>)}
        <div className="mt-8 w-full max-w-xs">
          <Button variant="outline" className="w-full justify-center" asChild>
            <Link to="/newsletter">Join Newsletter</Link>
          </Button>
        </div>
      </nav>
    </div>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="header" className={`dark sticky top-0 z-50 w-full border-b border-transparent transition-all duration-500 ${isScrolled ? "bg-background/80 backdrop-blur-[12px] border-border/40 shadow-sm" : "bg-background"}`}>
      <div className="container mx-auto px-4 h-20 flex items-center justify-between">
        {}
        <div className="flex-shrink-0">
          <Link to="/" className="block">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1559769/img/aia_monogram_logo.png" alt="AIAffiliate Logo" className="h-7 w-auto object-contain" />
          </Link>
        </div>

        {}
        <div className="hidden md:flex flex-1 justify-center">
          <MenuProvider menu_id="44">
            <DesktopNav />
          </MenuProvider>
        </div>

        {}
        <div className="flex items-center gap-4">
          <div className="hidden md:block">
            <Button variant="outline" size="sm" className="h-9 px-5 text-xs uppercase tracking-wider font-medium border-primary/20 hover:border-primary/50 hover:bg-primary/5 hover:text-primary transition-all" asChild>
              <Link to="/newsletter">Join Newsletter</Link>
            </Button>
          </div>

          {}
          <Button variant="ghost" size="icon" className="md:hidden text-foreground hover:text-primary hover:bg-transparent" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu">
            {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </Button>
        </div>
      </div>

      {}
      {isMobileMenuOpen && <MenuProvider menu_id="44">
          <MobileNav isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)} />
        </MenuProvider>}
    </header>;
}